import { JsonFormPropertyV2 } from '../component-library/FormComponent/types'
import { PageResponse, TContainer } from './types'

/* eslint-disable @typescript-eslint/no-explicit-any */
export function getPageFilterIds(pageFilters: { id: string }[]): string[] {
  return pageFilters.map((filter) => filter.id)
}

export function getMatchingFilters(
  filtersSelectionsPersisted: Record<string, any>[],
  pageFilters: { id: string; type: string; pinned?: string }[]
): Record<string, any>[] {
  return getMatchingSelections(filtersSelectionsPersisted, getPageFilterIds(pageFilters))
}

export function getMatchingSelections(
  filterSelections: Record<string, any>[],
  filterIds: string[]
): Record<string, any>[] {
  return filterSelections.filter((filterObj) => {
    const key = Object.keys(filterObj)[0]
    return filterIds.includes(key)
  })
}

export function getFilterSelectionsSignature(filterSelections: Record<string, any>[]): string {
  const normalizedSelections = filterSelections
    .map((filterObj) => {
      const key = Object.keys(filterObj)[0]
      return { key, value: filterObj[key] }
    })
    .sort((a, b) => a.key.localeCompare(b.key))

  return JSON.stringify(normalizedSelections)
}

export function joinNewAndPersistedFilters(
  filtersSelectionsPersisted: Record<string, any>[],
  defaultSelections: Record<string, any>[]
): Record<string, any>[] {
  // Create a deep copy of filtersSelectionsPersisted to avoid mutation
  const filteredSelectionsPersistedCopy = [...filtersSelectionsPersisted]

  // Set of keys already in filtersSelectionsPersisted
  const existingKeys = new Set(filtersSelectionsPersisted.map((obj) => Object.keys(obj)[0]))

  // Find selections that don't exist in filtersSelectionsPersisted
  const newFiltersWithSelections = defaultSelections.filter((selectionObj) => {
    const key = Object.keys(selectionObj)[0]
    return !existingKeys.has(key)
  })

  // Add the new selections to updatedFilters
  return [...filteredSelectionsPersistedCopy, ...newFiltersWithSelections]
}

export function getDefaultTileFilterSelections(properties: JsonFormPropertyV2[]): Record<string, any>[] {
  const selections = properties
    .filter((property) => property.default)
    .map((property) => {
      return {
        [property.id]: property.default,
      }
    })
  return selections
}

/**
 * Build Python code that reconstructs a Page using PageBuilder based on a serialized PageData JSON string.
 * The emitted code mirrors the style used in arq_showcase/catalog/pages/chart.py.
 */
export function buildPage(serializedPageData: string): string {
  // Defensive parse with minimal validation
  let page: unknown
  try {
    page = JSON.parse(serializedPageData)
  } catch (_e) {
    return '# Invalid JSON provided to buildPage()\n'
  }

  const isObject = (val: unknown): val is Record<string, unknown> => !!val && typeof val === 'object'

  if (!isObject(page)) {
    return '# Invalid PageData structure provided to buildPage()\n'
  }

  // Support PageResponse shape: { uiSchema: PageData }
  const root = (isObject((page as any).uiSchema) ? (page as any).uiSchema : page) as Record<string, unknown>

  const id = String(root.id ?? '')
  const title = root.title ? String(root.title) : ''
  const description = root.description ? String(root.description) : ''
  const autoSubmitPinnedFilters =
    typeof root.autoSubmitPinnedFilters === 'boolean' ? root.autoSubmitPinnedFilters : undefined
  const roles = Array.isArray(root.roles) ? (root.roles as unknown[]).map((r) => String(r)) : []
  const pageFilters = Array.isArray(root.pageFilters) ? (root.pageFilters as any[]) : []
  const gridLayout = isObject(root.gridLayout) ? (root.gridLayout as any) : undefined
  const containersInput =
    isObject(root) && Array.isArray((root as any).persistedContainers)
      ? ((root as any).persistedContainers as any[])
      : Array.isArray((root as any).containers)
        ? ((root as any).containers as any[])
        : []

  const pyStr = (s: string) => s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")

  // Map TS values to Python enums
  const mapPinned = (pinned?: string) => {
    if (pinned === 'left') return 'PinPosition.LEFT'
    if (pinned === 'right') return 'PinPosition.RIGHT'
    return 'PinPosition.NONE'
  }

  const mapFilterType = (t: string) => {
    if (t === 'filter') return 'PageFilterType.FILTER'
    if (t === 'cascading') return 'PageFilterType.CASCADING'
    return 'PageFilterType.FILTER'
  }

  const mapComponentType = (t: string) => {
    switch (t) {
      case 'form':
        return 'ComponentConfigType.FORM'
      case 'vega-lite-chart':
        return 'ComponentConfigType.VEGA_LITE_CHART'
      case 'table':
        return 'ComponentConfigType.TABLE'
      case 'card':
        return 'ComponentConfigType.CARD'
      case 'statement-editor':
        return 'ComponentConfigType.STATEMENT_EDITOR'
      case 'chat':
        return 'ComponentConfigType.CHAT'
      case 'data-form':
        return 'ComponentConfigType.DATA_FORM'
      case 'tab-container':
        return 'ComponentConfigType.TAB_CONTAINER'
      case 'md-text':
        return 'ComponentConfigType.MD_TEXT'
      case 'chart-sankey':
        return 'ComponentConfigType.CHART_SANKEY'
      case 'chart-radial':
        return 'ComponentConfigType.CHART_RADIAL'
      case 'chart-bar':
        return 'ComponentConfigType.CHART_BAR'
      case 'tree-navigator':
        return 'ComponentConfigType.TREE_NAVIGATOR'
      default:
        return 'ComponentConfigType.CARD'
    }
  }

  const lines: string[] = []
  lines.push('from catalog.platform import handler, FilterRequest')
  lines.push('from catalog.utils import fold_response')
  lines.push('')
  lines.push('from catalog.ui.page import PageBuilder')
  lines.push('from catalog.ui.models.page import PageFilterType, PinPosition, ComponentConfigType')
  lines.push('')
  lines.push('')
  lines.push(`@handler(id_='${pyStr(id)}', description='')`)
  lines.push(`def page_from_builder(filters: FilterRequest) -> dict:`)
  lines.push(`    page_builder = PageBuilder(`)
  lines.push(`        id='${pyStr(id)}',`)
  lines.push(`        title='${pyStr(title)}',`)
  lines.push(`        description='${pyStr(description)}',`)
  if (autoSubmitPinnedFilters !== undefined) {
    lines.push(`        autoSubmitPinnedFilters=${autoSubmitPinnedFilters ? 'True' : 'False'},`)
  }
  lines.push(`        roles=[${roles.map((r) => `'${pyStr(r)}'`).join(', ')}],`)
  lines.push(`    )`)
  lines.push('')

  // Filters
  if (pageFilters.length > 0) {
    for (const f of pageFilters) {
      if (!isObject(f)) continue
      const type = typeof f.type === 'string' ? f.type : 'filter'
      const fid = typeof f.id === 'string' ? f.id : ''
      const pinned = typeof (f as any).pinned === 'string' ? (f as any).pinned : undefined
      lines.push(`    page_builder.add_filter(${mapFilterType(type)}, '${pyStr(fid)}', ${mapPinned(pinned)})`)
    }
    lines.push('')
  }

  // Grid
  if (gridLayout && typeof gridLayout.cols === 'number' && typeof gridLayout.rows === 'number') {
    lines.push(`    page_builder.set_grid_layout(cols=${gridLayout.cols}, rows=${gridLayout.rows})`)
    lines.push('')
  }

  const parseContainerId = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
      const last = value.split('/').pop() || value
      const n = parseInt(last, 10)
      if (!Number.isNaN(n)) return n
    }
    return 1
  }

  // Containers
  for (const c of containersInput) {
    if (!isObject(c)) continue
    const cid = String((c as any).id ?? '')
    const pos = isObject((c as any).position) ? (c as any).position : undefined
    const comp = isObject((c as any).component) ? (c as any).component : undefined
    const colStart = pos && typeof pos.colStart === 'number' ? pos.colStart : 1
    const rowStart = pos && typeof pos.rowStart === 'number' ? pos.rowStart : 1
    const colSpan = pos && typeof pos.colSpan === 'number' ? pos.colSpan : 1
    const rowSpan = pos && typeof pos.rowSpan === 'number' ? pos.rowSpan : 1
    const ctype = comp && typeof comp.type === 'string' ? comp.type : 'card'
    const compId = comp && typeof comp.id === 'string' ? comp.id : ''

    lines.push(
      `    with page_builder.container(id=${parseContainerId(cid)}, rowStart=${rowStart}, colStart=${colStart}, rowSpan=${rowSpan}, colSpan=${colSpan}) as container:`
    )
    lines.push(`        container.set_component(${mapComponentType(ctype)}, '${pyStr(compId)}')`)
    lines.push('')
  }

  lines.push('    return fold_response(')
  lines.push('        ui_schema=page_builder.build(),')
  lines.push('        data={},')
  lines.push('        filters={},')
  lines.push('    )')

  return lines.join('\n') + '\n'
}

/**
 * @deprecated - this function is no longer used
 * Handles container persistence logic - checks for persisted containers in localStorage
 * and returns the appropriate uiSchema with either persisted or fresh containers
 */
export function handleContainerPersistence(data: PageResponse, appKey: string, pageId: string, projectId: number) {
  const containers = data.uiSchema.containers
  const persistedContainers = localStorage.getItem(`/page-layout/${appKey}${pageId}${projectId}`)

  if (persistedContainers && persistedContainers.length > 0) {
    const parsedContainers = JSON.parse(persistedContainers) as TContainer[]
    return {
      ...data.uiSchema,
      persistedContainers: parsedContainers,
    }
  }

  localStorage.setItem(`/page-layout/${appKey}${pageId}${projectId}`, JSON.stringify(containers))
  return { ...data.uiSchema, persistedContainers: containers }
}
